package ru.chm.gradereport.ui.screens.course.sheet

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import ru.chm.gradereport.data.GradeApi
import ru.chm.gradereport.model.CheckInSessionDto
import ru.chm.gradereport.model.LessonSessionDto
import ru.chm.gradereport.platform.copyTextToClipboard
import ru.chm.gradereport.platform.currentEpochMs
import ru.chm.gradereport.platform.currentPageBaseUrl
import ru.chm.gradereport.platform.downloadQrPngHighRes
import ru.chm.gradereport.platform.supportsQrPngDownload
import ru.chm.gradereport.ui.components.AppCard
import ru.chm.gradereport.ui.components.AppTextField
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.formatDisplayDate
import ru.chm.gradereport.ui.components.PrimaryButton
import ru.chm.gradereport.ui.components.SecondaryButton
import ru.chm.gradereport.ui.components.TabChip
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.util.imageBitmapFromPngBase64
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun CheckInQrDialog(
    api: GradeApi,
    courseId: String,
    sessions: List<LessonSessionDto>,
    onDismiss: () -> Unit,
    onError: (String?) -> Unit,
) {
    val colors = GradeTheme.colors
    val scope = rememberCoroutineScope()
    val pageBase = remember { currentPageBaseUrl() }
    var selectedSessionId by remember(sessions) {
        mutableStateOf(sessions.maxByOrNull { it.date }?.id ?: sessions.firstOrNull()?.id.orEmpty())
    }
    var ttlText by remember { mutableStateOf("10") }
    var active by remember { mutableStateOf<CheckInSessionDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var busy by remember { mutableStateOf(false) }
    var copied by remember { mutableStateOf(false) }
    var nowMs by remember { mutableStateOf(currentEpochMs()) }

    LaunchedEffect(courseId) {
        loading = true
        try {
            active = api.getActiveCheckIn(courseId, pageBase)
        } catch (e: Exception) {
            onError(e.message)
        } finally {
            loading = false
        }
    }

    LaunchedEffect(active?.expiresAtEpochMs) {
        while (active != null) {
            nowMs = currentEpochMs()
            delay(1000)
        }
    }

    val remainingSec = active?.let { ((it.expiresAtEpochMs - nowMs) / 1000L).coerceAtLeast(0) }
    val checkInUrl = active?.let { pageBase.trimEnd('/') + "/#" + it.checkInPath.trimStart('/') }
    val qrBitmap = remember(active?.qrPngBase64) {
        active?.qrPngBase64?.let { imageBitmapFromPngBase64(it) }
    }

    Dialog(onDismissRequest = onDismiss) {
        AppCard(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp)
                .widthIn(max = 480.dp),
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Text(
                    "QR посещаемость",
                    color = colors.textPrimary,
                    fontWeight = FontWeight.Bold,
                    fontSize = 20.sp,
                )
                MutedText("Один активный QR на курс. Новый код гасит предыдущий.")

                if (loading) {
                    MutedText("Загрузка…")
                } else if (active != null && remainingSec != null && remainingSec > 0) {
                    Text(
                        active!!.sessionLabel,
                        color = SessionAccent,
                        fontWeight = FontWeight.SemiBold,
                    )
                    Text(
                        "Действует ещё ${formatRemaining(remainingSec)}",
                        color = colors.textPrimary,
                        fontWeight = FontWeight.Medium,
                    )
                    if (qrBitmap != null) {
                        Image(
                            bitmap = qrBitmap,
                            contentDescription = "QR код посещаемости",
                            modifier = Modifier
                                .size(240.dp)
                                .align(Alignment.CenterHorizontally)
                                .padding(top = 4.dp),
                        )
                    } else {
                        MutedText("Не удалось показать QR — скопируйте ссылку ниже.")
                    }
                    if (!checkInUrl.isNullOrBlank()) {
                        MutedText(checkInUrl, modifier = Modifier.padding(top = 4.dp))
                        SecondaryButton(
                            text = if (copied) "Ссылка скопирована" else "Скопировать ссылку",
                            onClick = {
                                copyTextToClipboard(checkInUrl)
                                copied = true
                            },
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                    if (supportsQrPngDownload && qrBitmap != null) {
                        SecondaryButton(
                            text = "Скачать QR (высокое разрешение)",
                            onClick = {
                                active?.let { session ->
                                    val safeCourse = session.courseName
                                        .replace(Regex("[\\\\/:*?\"<>|]"), "-")
                                        .trim()
                                    downloadQrPngHighRes(
                                        fileName = "QR-посещаемость-${safeCourse.ifBlank { "курс" }}.png",
                                        pngBase64 = session.qrPngBase64,
                                    )
                                }
                            },
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        SecondaryButton(
                            text = "Погасить",
                            onClick = {
                                scope.launch {
                                    busy = true
                                    try {
                                        api.clearCheckIn(courseId)
                                        active = null
                                        copied = false
                                    } catch (e: Exception) {
                                        onError(e.message)
                                    } finally {
                                        busy = false
                                    }
                                }
                            },
                            enabled = !busy,
                            modifier = Modifier.weight(1f),
                        )
                        SecondaryButton(
                            text = "Закрыть",
                            onClick = onDismiss,
                            modifier = Modifier.weight(1f),
                        )
                    }
                } else {
                    if (active != null) {
                        MutedText("Предыдущий QR истёк — создайте новый.")
                    }
                    Text("Занятие", color = colors.textPrimary, fontWeight = FontWeight.SemiBold)
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        sessions
                            .sortedWith(compareByDescending<LessonSessionDto> { it.date }.thenBy { it.title })
                            .forEach { session ->
                                val label = buildString {
                                    append(formatDisplayDate(session.date))
                                    if (session.title.isNotBlank()) {
                                        append(" — ")
                                        append(session.title)
                                    }
                                }
                                TabChip(
                                    text = label,
                                    selected = session.id == selectedSessionId,
                                    onClick = { selectedSessionId = session.id },
                                )
                            }
                    }
                    AppTextField(
                        value = ttlText,
                        onValueChange = { ttlText = it.filter { ch -> ch.isDigit() }.take(3) },
                        label = "Время жизни (минуты)",
                    )
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        SecondaryButton(
                            text = "Отмена",
                            onClick = onDismiss,
                            modifier = Modifier.weight(1f),
                        )
                        PrimaryButton(
                            text = if (busy) "Создание…" else "Создать QR",
                            enabled = !busy && selectedSessionId.isNotBlank() &&
                                (ttlText.toIntOrNull() ?: 0) in 1..180,
                            onClick = {
                                scope.launch {
                                    busy = true
                                    try {
                                        active = api.createCheckIn(
                                            courseId = courseId,
                                            sessionId = selectedSessionId,
                                            ttlMinutes = ttlText.toIntOrNull() ?: 10,
                                            pageBaseUrl = pageBase,
                                        )
                                        copied = false
                                    } catch (e: Exception) {
                                        onError(e.message)
                                    } finally {
                                        busy = false
                                    }
                                }
                            },
                            modifier = Modifier.weight(1f),
                        )
                    }
                }
            }
        }
    }
}

private fun formatRemaining(totalSec: Long): String {
    val m = totalSec / 60
    val s = totalSec % 60
    return if (m > 0) "${m} мин ${s.toString().padStart(2, '0')} с" else "$s с"
}
